import { useState, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import {
  Select,
  Checkbox,
  InputText,
  Currency,
  IconSearch,
  InputSize,
  PaymentType as PaymentTypeProps,
  IconAnimatedDots,
} from '@ui/src';
import { useAppSelector } from '@store';
import { PaymentType } from '../../store/dictionaries/types';
import { ExternalAccountFormTypes } from './types';
import FormRow from './form-row';

import cn from './styles.module.css';

const ExternalAccountForm = ({
  externalAccount,
  availablePaymentTypes,
  control,
  errors,
  pspPartnerId,
  paymentTypes,
  paymentType,
  setSelectedPartner,
  pspPartners,
  selectedPartner,
  setValue,
  currencyId,
  accounts,
  onSearchAccounts,
  onClear,
  accountsIsLoading,
  balances,
  accountId,
  customerAccountId,
  casp,
  isOpenPayd,
  integrationType,
  setIntegrationType,
  openPaydIntegration,
  balancesIsLoading,
  isPrimeVault,
  caspId,
  caspValues,
  setIsOpenPayd,
  setIsPrimeVault,
  isKeel,
  setIsKeel,
}: ExternalAccountFormTypes) => {
  const [filteredPaymentTypes, setFilteredPaymentTypes] = useState<
    PaymentType[]
  >([]);
  const [currencies, setCurrencies] = useState<Currency[]>();
  const [selectedPaymentType, setSelectedPaymentType] = useState<PaymentType>();
  const [searchValue, setSearchValue] = useState('');
  const [availableCurrencies, setAvailableCurrencies] = useState<Currency[]>(
    [],
  );

  const { currencies: dictionariesCurrencies } = useAppSelector(
    (state) => state.root,
  );

  const onSearch = (value: string) => {
    setSearchValue(value);

    const nextCurrencies = value
      ? availableCurrencies?.filter((item) =>
          item.name.toLowerCase().includes(value.toLowerCase()),
        )
      : availableCurrencies?.map((item) => item);

    setCurrencies(nextCurrencies);
  };

  useEffect(() => {
    if (selectedPartner && paymentTypes) {
      const nextPaymentTypes: PaymentType[] = [];

      paymentTypes?.forEach((item) => {
        if (
          selectedPartner?.paymentsTypes?.find(
            (type) => type === item.paymentType,
          )
        ) {
          nextPaymentTypes.push(item);
        }
      });

      setFilteredPaymentTypes(nextPaymentTypes);
    }
  }, [paymentTypes, selectedPartner]);

  useEffect(() => {
    if (
      !accountId &&
      (isOpenPayd || isKeel) &&
      integrationType === 'with-integration' &&
      openPaydIntegration &&
      balances
    ) {
      const integrationCurrencies: { name: string; id: string }[] = [];

      openPaydIntegration?.pspIntegrationPayments?.forEach((item) => {
        item.currencies?.forEach((currencyItem) => {
          if (
            !integrationCurrencies?.find(
              (integrationCurrency) =>
                integrationCurrency.id === currencyItem.id,
            )
          ) {
            integrationCurrencies.push(currencyItem.currency);
          }
        });
      });

      if (integrationCurrencies?.length) {
        const nextCurrenciesList: Currency[] = [];

        integrationCurrencies?.forEach((item) => {
          if (
            balances?.find((balance) => balance.currencyId === item.id) &&
            !nextCurrenciesList.find((currency) => currency.id === item.id)
          ) {
            nextCurrenciesList.push(item as Currency);
          }
        });

        setCurrencies(nextCurrenciesList);
        setAvailableCurrencies(nextCurrenciesList);
      }
    }
  }, [
    isOpenPayd,
    integrationType,
    openPaydIntegration,
    balances,
    accountId,
    isKeel,
  ]);

  useEffect(() => {
    if (
      !accountId &&
      filteredPaymentTypes?.length &&
      paymentType &&
      selectedPaymentType &&
      balances &&
      ((!isOpenPayd && !isKeel) ||
        ((isOpenPayd || isKeel) && integrationType === 'without-integration'))
    ) {
      const nextCurrenciesList: Currency[] = [];

      selectedPaymentType?.currencies?.edges?.forEach((item) => {
        if (balances?.find((balance) => balance.currencyId === item.node.id)) {
          nextCurrenciesList.push(item.node);
        }
      });

      setCurrencies(nextCurrenciesList);
      setAvailableCurrencies(nextCurrenciesList);
    }
  }, [
    paymentType,
    filteredPaymentTypes,
    selectedPaymentType,
    balances,
    accountId,
    isOpenPayd,
    isKeel,
    integrationType,
  ]);

  useEffect(() => {
    if (
      externalAccount &&
      paymentTypes &&
      balances &&
      accountId &&
      dictionariesCurrencies
    ) {
      const types: string[] = [];
      const nextCurrenciesList: Currency[] = [];

      externalAccount?.pspIntegrationPayment?.paymentsTypes?.forEach((item) => {
        if (paymentTypes?.find((typeValue) => typeValue.paymentType === item)) {
          types.push(
            paymentTypes?.find((typeValue) => typeValue.paymentType === item)
              ?.name as string,
          );
        }
      });

      if (!isOpenPayd && !isKeel) {
        const currentPaymentType = paymentTypes?.find(
          (item) =>
            item.paymentType ===
            externalAccount?.pspIntegrationPayment?.paymentsTypes[0],
        );

        if (currentPaymentType) {
          if (
            currentPaymentType.paymentType !==
              PaymentTypeProps.CRYPTO_ASSET_PAYMENT ||
            (currentPaymentType.paymentType ===
              PaymentTypeProps.CRYPTO_ASSET_PAYMENT &&
              !externalAccount.caspId)
          ) {
            currentPaymentType?.currencies?.edges?.forEach((item) => {
              if (
                balances?.find((balance) => balance.currencyId === item.node.id)
              ) {
                nextCurrenciesList.push(item.node);
              }
            });
          } else if (
            currentPaymentType.paymentType ===
              PaymentTypeProps.CRYPTO_ASSET_PAYMENT &&
            externalAccount.caspId &&
            caspValues
          ) {
            const currentCasp = caspValues?.find(
              (item) => item.id === externalAccount.caspId,
            );

            if (currentCasp) {
              const caspCurrencies = currentCasp?.currencies?.map(
                (item) => item.currency,
              );

              if (caspCurrencies?.length) {
                caspCurrencies?.forEach((item) => {
                  if (
                    balances?.find((balance) => balance.currencyId === item.id)
                  ) {
                    nextCurrenciesList.push(item);
                  }
                });
              }
            }
          }
        }
      }

      if ((isOpenPayd || isKeel) && openPaydIntegration) {
        const integrationCurrencies: { name: string; id: string }[] = [];

        openPaydIntegration?.pspIntegrationPayments?.forEach((item) => {
          item.currencies?.forEach((currencyItem) => {
            if (
              !integrationCurrencies?.find(
                (integrationCurrency) =>
                  integrationCurrency.id === currencyItem.id,
              )
            ) {
              integrationCurrencies.push(currencyItem.currency);
            }
          });
        });

        if (integrationCurrencies?.length) {
          integrationCurrencies?.forEach((item) => {
            if (
              balances?.find((balance) => balance.currencyId === item.id) &&
              !nextCurrenciesList.find((currency) => currency.id === item.id)
            ) {
              nextCurrenciesList.push(item as Currency);
            }
          });
        }
      }

      if (nextCurrenciesList?.length) {
        setCurrencies(nextCurrenciesList);
        setAvailableCurrencies(nextCurrenciesList);
      }
    }
  }, [
    externalAccount,
    paymentTypes,
    balances,
    dictionariesCurrencies,
    isOpenPayd,
    isKeel,
    integrationType,
    openPaydIntegration,
  ]);

  useEffect(() => {
    if (!accountId && caspValues && caspId) {
      const currentCasp = caspValues?.find((item) => item.id === caspId);

      if (currentCasp) {
        const caspCurrencies = currentCasp?.currencies?.map(
          (item) => item.currency,
        );

        if (caspCurrencies?.length) {
          const nextCurrenciesList: Currency[] = [];

          caspCurrencies?.forEach((item) => {
            if (balances?.find((balance) => balance.currencyId === item.id)) {
              nextCurrenciesList.push(item);
            }
          });

          setCurrencies(nextCurrenciesList);
          setAvailableCurrencies(nextCurrenciesList);
        }
      }
    }
  }, [isPrimeVault, caspValues, caspId]);

  const currenciesListIsVisible =
    Boolean(
      (isOpenPayd || isKeel) &&
      integrationType === 'with-integration' &&
      customerAccountId,
    ) ||
    Boolean(
      (pspPartnerId &&
        paymentType &&
        !isOpenPayd &&
        !isPrimeVault &&
        !isKeel) ||
      ((isOpenPayd || isKeel) &&
        integrationType === 'without-integration' &&
        Boolean(pspPartnerId && paymentType)) ||
      (isPrimeVault &&
        integrationType === 'without-integration' &&
        Boolean(pspPartnerId && paymentType)),
    ) ||
    Boolean(isPrimeVault && integrationType === 'with-integration' && caspId) ||
    accountId;

  return (
    <div className={cn.externalCreateForm}>
      <div className={cn.formValues}>
        <Controller
          control={control}
          defaultValue=""
          name="status"
          render={({ field: { value, name, onChange } }) => (
            <Select
              name={name}
              value={value}
              title="Status"
              list={[
                {
                  name: 'Enabled',
                  value: 'enabled',
                },
                {
                  name: 'Disabled',
                  value: 'disabled',
                },
              ]}
              onChange={onChange}
              error={errors?.status?.message}
              fullWidth
            />
          )}
        />
      </div>
      {accountId &&
        externalAccount?.pspIntegrationPayment?.paymentsTypes?.includes(
          PaymentTypeProps.CRYPTO_ASSET_PAYMENT,
        ) && (
          <div className={cn.formValues}>
            <Controller
              control={control}
              defaultValue=""
              name="caspId"
              render={({ field: { value, name, onChange } }) => (
                <Select
                  name={name}
                  value={value}
                  title="CASP"
                  list={casp?.map((item) => ({
                    name:
                      selectedPartner &&
                      selectedPartner?.caspId &&
                      !externalAccount?.caspId &&
                      selectedPartner?.caspId === item.value
                        ? `Default from Bank ${item.name}`
                        : item.name,
                    value: item.value,
                  }))}
                  onChange={onChange}
                  error={errors?.caspId?.message}
                  placeholder="Select CASP"
                  disabled={Boolean(accountId)}
                  withClearButton
                  fullWidth
                />
              )}
            />
          </div>
        )}

      <div className={accountId ? cn.infoBlock : ''}>
        {accountId && (
          <FormRow
            accountId={accountId}
            type="text"
            inputValue={externalAccount?.iban}
            inputName="iban"
            control={control}
            title="IBAN"
          />
        )}
        <FormRow
          accountId={accountId}
          type="select"
          inputValue={externalAccount?.pspPartner?.companyName}
          inputName="pspPartnerId"
          list={
            pspPartners?.map((item) => ({
              name: item.companyName,
              value: item.id,
            })) || []
          }
          control={control}
          setSelectedPartner={setSelectedPartner}
          error={errors?.pspPartnerId?.message}
          setValue={setValue}
          title="Bank"
          placeholder="Select Bank"
          pspPartners={pspPartners}
          link={
            accountId ? `/bank-and-psp/${externalAccount?.pspPartner?.id}` : ''
          }
          setIsOpenPayd={setIsOpenPayd}
          setIsPrimeVault={setIsPrimeVault}
          setIsKeel={setIsKeel}
          withClearButton
        />
        {(isOpenPayd || isPrimeVault || isKeel) && !accountId && (
          <div className={cn.integrationTypeWrapper}>
            <Checkbox
              type="radio"
              name="without-integration"
              checked={integrationType === 'without-integration'}
              title="Without integration"
              onChange={() => {
                setIntegrationType('without-integration');
                setAvailableCurrencies([]);
                setCurrencies([]);

                setValue('currencyId', []);

                if (isPrimeVault && caspId) {
                  setValue('caspId', '');
                }
              }}
              labelClassName={cn.withMargin}
            />
            <Checkbox
              type="radio"
              name="with-integration"
              checked={integrationType === 'with-integration'}
              title="With integration"
              onChange={() => {
                setIntegrationType('with-integration');
                setAvailableCurrencies([]);
                setCurrencies([]);
                setValue('currencyId', []);

                if (isPrimeVault && caspId) {
                  setValue('caspId', '');
                }
              }}
            />
          </div>
        )}
        <FormRow
          accountId={accountId}
          type="autocomplete"
          inputValue={externalAccount?.account?.fullNumber}
          inputName="accountId"
          list={accounts || []}
          control={control}
          error={errors?.accountId?.message}
          title="Customer Account"
          placeholder="Select Account"
          onClear={onClear}
          listIsLoading={accountsIsLoading}
          onSearchList={onSearchAccounts}
          link={accountId ? `/accounts/list/${externalAccount?.accountId}` : ''}
          withSuffix
        />
        {!accountId &&
          ((!isOpenPayd && !isPrimeVault && !isKeel) ||
            ((isOpenPayd || isKeel) &&
              integrationType === 'without-integration') ||
            (isPrimeVault && integrationType === 'without-integration')) && (
            <FormRow
              type="text"
              inputName="iban"
              control={control}
              error={errors?.iban?.message}
              title="External Account number"
              placeholder="Enter number"
            />
          )}

        {accountId && (
          <FormRow
            accountId={accountId}
            inputName="iban"
            control={control}
            title="Customer"
            inputValue={externalAccount?.account?.description}
            type="text"
            link={
              // eslint-disable-next-line no-nested-ternary
              accountId
                ? externalAccount?.account?.organization?.type === 'CORPORATE'
                  ? `/customers/corporate/${externalAccount?.account?.organizationId}`
                  : `/customers/individual/${externalAccount?.account?.organizationId}`
                : ''
            }
          />
        )}
        {((!isOpenPayd && !isPrimeVault && !isKeel) ||
          ((isOpenPayd || isKeel) &&
            integrationType === 'without-integration') ||
          (isPrimeVault && integrationType === 'without-integration')) && (
          <FormRow
            accountId={accountId}
            type="select"
            inputValue={availablePaymentTypes?.join(', ')}
            inputName="paymentType"
            list={
              filteredPaymentTypes?.map((item) => ({
                name: item.name,
                value: item.paymentType,
              })) || []
            }
            control={control}
            error={errors?.paymentType?.message}
            setSelectedPaymentType={setSelectedPaymentType}
            title="Payment Type"
            placeholder="Select Payment Type"
            disabled={!pspPartnerId || !customerAccountId}
            filteredPaymentTypes={filteredPaymentTypes}
            withClearButton
          />
        )}
        {((paymentType &&
          paymentType === PaymentTypeProps.CRYPTO_ASSET_PAYMENT &&
          !accountId) ||
          (isPrimeVault && integrationType === 'with-integration')) && (
          <FormRow
            accountId={accountId}
            type="select"
            inputName="caspId"
            list={casp?.map((item) => ({
              name:
                selectedPartner &&
                selectedPartner?.caspId &&
                !accountId &&
                !externalAccount?.caspId &&
                selectedPartner?.caspId === item.value
                  ? `Default from Bank (${item.name})`
                  : item.name,
              value: item.value,
            }))}
            control={control}
            error={errors?.caspId?.message}
            title="CASP"
            placeholder="Select CASP"
            disabled={
              (!pspPartnerId || !paymentType || !customerAccountId) &&
              (!isPrimeVault ||
                (isPrimeVault && integrationType === 'without-integration') ||
                (isPrimeVault &&
                  integrationType === 'with-integration' &&
                  !customerAccountId))
            }
            withClearButton
          />
        )}
      </div>

      <div className={cn.currenciesWrapper}>
        <div className={cn.currenciesTitle}>Available currencies</div>
        {(isOpenPayd || isKeel) &&
          integrationType === 'with-integration' &&
          !customerAccountId && (
            <div className={cn.selectValues}>Select Customer Account</div>
          )}
        {!accountId &&
          (!pspPartnerId || !paymentType) &&
          ((!isOpenPayd && !isPrimeVault && !isKeel) ||
            ((isOpenPayd || isKeel) &&
              integrationType === 'without-integration') ||
            (isPrimeVault && integrationType === 'without-integration')) && (
            <div className={cn.selectValues}>Select Bank and Payment Type</div>
          )}
        {!accountId &&
          isPrimeVault &&
          integrationType === 'with-integration' &&
          !caspId && <div className={cn.selectValues}>Select CASP</div>}
        {balancesIsLoading && (
          <div className={cn.balancesLoader}>
            Loading <IconAnimatedDots width={26} height={26} />
          </div>
        )}
        {!balancesIsLoading && currenciesListIsVisible && (
          <>
            {currencies && currencies?.length > 0 && (
              <>
                <div className={cn.searchWrapper}>
                  <IconSearch
                    className={cn.searchIcon}
                    width={16}
                    height={16}
                  />
                  <InputText
                    name="search"
                    size={InputSize.SMALL}
                    placeholder="Enter currency"
                    className={cn.searchInput}
                    value={searchValue}
                    onChange={(e) => onSearch(e.target.value)}
                  />
                </div>
                {!accountId && (
                  <div className={cn.selectAllCheckboxWrapper}>
                    <Checkbox
                      name="select-all"
                      checked={Boolean(
                        currencyId?.length === currencies?.length,
                      )}
                      type="checkbox"
                      title="Select all"
                      onChange={(e) => {
                        const currenciesList = e.target.checked
                          ? currencies?.map((item) => item.id)
                          : [];

                        setValue('currencyId', currenciesList);
                      }}
                    />
                  </div>
                )}
              </>
            )}
            {!accountId &&
              !currencies?.length &&
              pspPartnerId &&
              customerAccountId &&
              ((!isOpenPayd && !isKeel && paymentType) ||
                ((isOpenPayd || isKeel) &&
                  integrationType === 'without-integration' &&
                  paymentType) ||
                (!isPrimeVault && paymentType) ||
                (isPrimeVault &&
                  integrationType === 'without-integration' &&
                  paymentType)) && (
                <div className={cn.selectValues}>
                  Based on the account currencies and the bank integration,
                  there are no available currencies for creating an external
                  account
                </div>
              )}
            <Controller
              control={control}
              name="currencyId"
              render={({ field: { value, name, onChange } }) => (
                <div>
                  {accountId &&
                    dictionariesCurrencies
                      ?.filter((item) => currencyId?.includes(item.id))
                      ?.map((item) => (
                        <div className={cn.checkboxWrapper} key={item.id}>
                          <Checkbox
                            name={`${name}-${item.id}`}
                            type="checkbox"
                            checked={Boolean(
                              value?.find((currency) => currency === item.id),
                            )}
                            title={item.name}
                            onChange={(e) => {
                              let nextCurrencies = ([] as string[]).concat(
                                value,
                              );

                              if (e.target.checked) {
                                nextCurrencies.push(item.id);
                              } else {
                                nextCurrencies = nextCurrencies?.filter(
                                  (currency) => currency !== item.id,
                                );
                              }

                              onChange(nextCurrencies);
                            }}
                            disabled={Boolean(
                              accountId && (isOpenPayd || isKeel),
                            )}
                          />
                        </div>
                      ))}
                  {currencies
                    ?.filter((item) =>
                      accountId ? !value.includes(item.id) : item,
                    )
                    ?.map((item) => (
                      <div className={cn.checkboxWrapper} key={item.id}>
                        <Checkbox
                          name={`${name}-${item.id}`}
                          type="checkbox"
                          checked={Boolean(
                            value?.find((currency) => currency === item.id),
                          )}
                          title={item.name}
                          onChange={(e) => {
                            let nextCurrencies = ([] as string[]).concat(value);

                            if (e.target.checked) {
                              nextCurrencies.push(item.id);
                            } else {
                              nextCurrencies = nextCurrencies?.filter(
                                (currency) => currency !== item.id,
                              );
                            }

                            onChange(nextCurrencies);
                          }}
                          disabled={Boolean(
                            accountId && (isOpenPayd || isKeel),
                          )}
                        />
                      </div>
                    ))}
                </div>
              )}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default ExternalAccountForm;
